import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, Monitor } from "lucide-react";
import { formatErr } from "@/lib/api";
import { desk } from "@/pages/admin/desktop/deskApi";
import { takeDesktopGoogleInvite } from "./DesktopGoogleButton";

export default function DesktopGoogleCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const processedRef = useRef(false);
  const inviteTokenRef = useRef(takeDesktopGoogleInvite());
  const sessionId = new URLSearchParams(location.hash.startsWith("#") ? location.hash.slice(1) : location.hash).get("session_id");
  const [error, setError] = useState("");
  const [onboarding, setOnboarding] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    if (!sessionId) { setError("Missing Google session. Please try again."); return; }
    const exchange = async () => {
      try {
        const { data } = await desk.post("/desktop/auth/google-callback", {
          session_id: sessionId,
          invite_token: inviteTokenRef.current || undefined,
        });
        window.history.replaceState(null, "", location.pathname);
        if (data.needs_profile) {
          setOnboarding(data);
          setBusinessName(data.google_name || "");
          setBusinessEmail(data.login_email || "");
          return;
        }
        localStorage.setItem("desktop_token", data.token);
        navigate("/desktop", { replace: true });
      } catch (exc) {
        setError(formatErr(exc.response?.data?.detail) || exc.message || "Google sign-in failed.");
      }
    };
    exchange();
  }, [location.pathname, navigate, sessionId]);

  const completeProfile = async (event) => {
    event.preventDefault();
    setError(""); setSaving(true);
    try {
      const { data } = await desk.post("/desktop/auth/google-activate", {
        onboarding_token: onboarding.onboarding_token,
        business_name: businessName,
        business_email: businessEmail,
      });
      localStorage.setItem("desktop_token", data.token);
      navigate("/desktop", { replace: true });
    } catch (exc) {
      setError(formatErr(exc.response?.data?.detail) || exc.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "radial-gradient(120% 90% at 20% 10%, #2563eb 0%, #1e3a8a 38%, #0f172a 100%)" }}>
      <div className="w-full max-w-md rounded-xl bg-white/95 p-6 shadow-2xl backdrop-blur" data-testid="desk-google-callback-screen">
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100"><Monitor className="h-5 w-5 text-sky-600" /></div>
          <div><h1 className="text-lg font-bold text-slate-800">Desktop Google sign-in</h1><p className="text-xs text-slate-500">Your private documents and e-sign workspace</p></div>
        </div>
        {error && <p className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="desk-google-callback-error">{error}</p>}
        {!error && !onboarding && <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500" data-testid="desk-google-callback-loading"><Loader2 className="h-4 w-4 animate-spin" /> Finishing Google sign-in…</div>}
        {onboarding && (
          <form onSubmit={completeProfile} className="space-y-4" data-testid="desk-google-profile-form">
            <p className="text-sm text-slate-600">Finish your invited Desktop profile. No password is required.</p>
            <div><label className="text-xs font-semibold text-slate-600">Business name</label><input required value={businessName} onChange={(event) => setBusinessName(event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="desk-google-business-name" /></div>
            <div><label className="text-xs font-semibold text-slate-600">Business email</label><input type="email" required value={businessEmail} onChange={(event) => setBusinessEmail(event.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="desk-google-business-email" /></div>
            <button type="submit" disabled={saving} className="min-h-11 w-full rounded-md bg-sky-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-sky-700 disabled:opacity-60" data-testid="desk-google-profile-submit">{saving ? "Opening Desktop…" : "Finish & open my Desktop"}</button>
          </form>
        )}
        {error && <button type="button" onClick={() => navigate("/desktop", { replace: true })} className="min-h-11 w-full rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="desk-google-callback-back">Back to Desktop sign-in</button>}
      </div>
    </div>
  );
}